/**
 * Julgamento da reunião de camada.
 * Disciplina: o líder compareceu à rotina.
 * Conformidade: o operador auditado estava no padrão.
 * Resultado: a meta do indicador foi atingida.
 * Sem a ida do líder, conformidade e resultado não são avaliados.
 */

import type { Indicator } from '../../../types/indicator';
import type { LstExecucao, LstTemplate } from '../types';
import { isTemplateDueOnDate } from './lstSchedule';
import { formatTimeHHmm } from './lstMatrixHourly';
import { isLstSlotStillOpen } from '../utils/lstEffectiveStatus';
import { resolveTemplateIndicator } from '../utils/lstIndicator';
import { computeAchievementPct } from './lstCorrelation';

export type LayerVerdict = 'ok' | 'falha' | 'nao_avaliado';

export interface LayerAuditRoutineRow {
  templateId: string;
  title: string;
  timeLabel: string;
  closed: number;
  attended: number;
  missed: number;
  onStandard: number;
  offStandard: number;
  discipline: LayerVerdict;
  disciplineLabel: string;
  conformity: LayerVerdict;
  conformityLabel: string;
  result: LayerVerdict;
  resultLabel: string;
}

export interface LayerAuditJudgment {
  rows: LayerAuditRoutineRow[];
  disciplinePct: number | null;
  conformityPct: number | null;
  resultPct: number | null;
}

export interface BuildLayerAuditInput {
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  dates: string[];
  today: string;
  indicators?: Indicator[];
}

function pct(part: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.round((part / total) * 100);
}

function disciplineOf(attended: number, missed: number): { verdict: LayerVerdict; label: string } {
  const closed = attended + missed;
  if (closed === 0) return { verdict: 'nao_avaliado', label: 'No prazo' };
  if (missed === 0) return { verdict: 'ok', label: 'Compareceu' };
  if (attended === 0) return { verdict: 'falha', label: 'Faltou' };
  return { verdict: 'falha', label: `Faltou ${missed} de ${closed}` };
}

function conformityOf(attended: number, onStandard: number, offStandard: number): { verdict: LayerVerdict; label: string } {
  if (attended === 0 || onStandard + offStandard === 0) {
    return { verdict: 'nao_avaliado', label: '—' };
  }
  if (offStandard === 0) return { verdict: 'ok', label: 'No padrão' };
  if (onStandard === 0) return { verdict: 'falha', label: 'Fora do padrão' };
  return { verdict: 'falha', label: `Desvio em ${offStandard}` };
}

export function buildLayerAudit(input: BuildLayerAuditInput): LayerAuditJudgment {
  const { templates, execucoes, dates, today, indicators = [] } = input;
  const execBySlot = new Map<string, LstExecucao>();
  for (const exec of execucoes) {
    execBySlot.set(`${exec.template_id}__${exec.data}`, exec);
  }

  let attendedTotal = 0;
  let missedTotal = 0;
  let onStandardTotal = 0;
  let offStandardTotal = 0;
  let resultOk = 0;
  let resultJudged = 0;

  const rows = templates
    .filter((template) => template.active)
    .slice()
    .sort((a, b) => (a.scheduled_time ?? '99:99').localeCompare(b.scheduled_time ?? '99:99') || a.title.localeCompare(b.title))
    .map((template) => {
      let attended = 0;
      let missed = 0;
      let onStandard = 0;
      let offStandard = 0;
      const values: number[] = [];

      for (const date of dates) {
        if (!isTemplateDueOnDate(template, date, dates)) continue;
        if (isLstSlotStillOpen(template.scheduled_time, date, today)) continue;

        const exec = execBySlot.get(`${template.id}__${date}`);
        const status = exec?.status;
        if (status === 'realizado' || status === 'realizado_com_desvio' || status === 'processo_parado') {
          attended += 1;
          if (status === 'realizado') onStandard += 1;
          if (status === 'realizado_com_desvio') offStandard += 1;
          if (status !== 'processo_parado' && exec?.indicator_realized_value != null && Number.isFinite(exec.indicator_realized_value)) {
            values.push(exec.indicator_realized_value);
          }
        } else {
          missed += 1;
        }
      }

      const discipline = disciplineOf(attended, missed);
      const conformity = conformityOf(attended, onStandard, offStandard);
      const link = resolveTemplateIndicator(template, indicators);

      let result: LayerVerdict = 'nao_avaliado';
      let resultLabel = '—';
      if (attended > 0 && link.hasLink && link.target != null && values.length > 0) {
        const average = values.reduce((sum, value) => sum + value, 0) / values.length;
        const achievement = computeAchievementPct(average, link.target, link.direction);
        result = achievement >= 100 ? 'ok' : 'falha';
        resultLabel = result === 'ok' ? 'Na meta' : 'Fora da meta';
        resultJudged += 1;
        if (result === 'ok') resultOk += 1;
      }

      attendedTotal += attended;
      missedTotal += missed;
      onStandardTotal += onStandard;
      offStandardTotal += offStandard;

      return {
        templateId: template.id,
        title: template.title,
        timeLabel: formatTimeHHmm(template.scheduled_time) ?? '',
        closed: attended + missed,
        attended,
        missed,
        onStandard,
        offStandard,
        discipline: discipline.verdict,
        disciplineLabel: discipline.label,
        conformity: conformity.verdict,
        conformityLabel: conformity.label,
        result,
        resultLabel,
      };
    })
    .filter((row) => row.closed > 0);

  return {
    rows,
    disciplinePct: pct(attendedTotal, attendedTotal + missedTotal),
    conformityPct: pct(onStandardTotal, onStandardTotal + offStandardTotal),
    resultPct: pct(resultOk, resultJudged),
  };
}
